import React, { useMemo } from 'react';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import DOMPurify from 'dompurify';
import {
  AggregatorCredentialsFieldType,
  type AggregatorCredentialFormField,
} from '@/utils/aggregatorCredentials';

interface CredentialFieldProps {
  field: AggregatorCredentialFormField;
  value: string;
  onChange: (fieldName: string, value: string) => void;
}

/**
 * Renders one backend-described credential field. The aggregator never appears
 * here: the backend says what to ask, this decides how to draw it. Ported from
 * legacy `CredentialsForm.jsx:123-167`, with two deliberate differences — HTML is
 * sanitized (legacy injected it raw) and unsupported types render nothing instead
 * of falling through to a text input.
 */
export const CredentialField: React.FC<CredentialFieldProps> = ({
  field,
  value,
  onChange,
}) => {
  const sanitizedHtml = useMemo(
    () =>
      field.HTML
        ? DOMPurify.sanitize(field.HTML, { USE_PROFILES: { html: true } })
        : '',
    [field.HTML],
  );

  switch (field.FieldType) {
    case AggregatorCredentialsFieldType.Text:
    case AggregatorCredentialsFieldType.Password:
      return (
        <TextField
          fullWidth
          helperText={field.Instructions ?? undefined}
          label={field.Display}
          margin='normal'
          onChange={(event) => {
            onChange(field.FieldName, event.target.value);
          }}
          type={
            field.FieldType === AggregatorCredentialsFieldType.Password
              ? 'password'
              : 'text'
          }
          value={value}
        />
      );

    case AggregatorCredentialsFieldType.RadioGroup:
      return (
        <FormControl
          fullWidth
          margin='normal'
        >
          <FormLabel>{field.Display}</FormLabel>
          <RadioGroup
            onChange={(event) => {
              onChange(field.FieldName, event.target.value);
            }}
            value={value}
          >
            {(field.Choices ?? []).map((choice) => (
              <FormControlLabel
                control={<Radio />}
                key={choice.Value}
                label={choice.Label}
                value={choice.Value}
              />
            ))}
          </RadioGroup>
          {field.Instructions ? (
            <FormHelperText>{field.Instructions}</FormHelperText>
          ) : null}
        </FormControl>
      );

    case AggregatorCredentialsFieldType.Checkboxgroup: {
      // One field carries one string, so multi-select is comma-joined. The
      // backend contract does not describe this; it is our encoding choice.
      const selected = value ? value.split(',') : [];
      const toggle = (choiceValue: string, checked: boolean): void => {
        const next = checked
          ? [...selected, choiceValue]
          : selected.filter((entry) => entry !== choiceValue);
        onChange(field.FieldName, next.join(','));
      };

      return (
        <FormControl
          fullWidth
          margin='normal'
        >
          <FormLabel>{field.Display}</FormLabel>
          <FormGroup>
            {(field.Choices ?? []).map((choice) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selected.includes(choice.Value)}
                    onChange={(event) => {
                      toggle(choice.Value, event.target.checked);
                    }}
                  />
                }
                key={choice.Value}
                label={choice.Label}
              />
            ))}
          </FormGroup>
          {field.Instructions ? (
            <FormHelperText>{field.Instructions}</FormHelperText>
          ) : null}
        </FormControl>
      );
    }

    case AggregatorCredentialsFieldType.Image: {
      // An image challenge (captcha) arrives in one of three shapes. HTML wins
      // when present, because the aggregator controls the markup.
      if (sanitizedHtml) {
        return (
          <span
            className='credential-field-image'
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          />
        );
      }

      if (!field.Image) {
        return null;
      }

      // `byte[]` server-side, so it is normally bare base64 with no MIME prefix.
      // Anchored to the start of the string on purpose: legacy used
      // `indexOf("data") === -1`, which misfires when base64 happens to contain
      // those letters anywhere.
      const src = field.Image.startsWith('data:')
        ? field.Image
        : `data:image/png;base64,${field.Image}`;

      return (
        <img
          alt={field.Display}
          className='credential-field-image'
          src={src}
          style={{ display: 'block', maxWidth: '100%' }}
        />
      );
    }

    default:
      // Link/OAuth is out of scope for this flow; the orchestrator turns an
      // unsupported form into an explicit terminal state.
      return null;
  }
};
